import React, { useContext, createContext } from 'react';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { ToastSnackbar } from '../components/ToastSnackbar';
import { ToastProps } from '../types/ToastProps';
import { pushToast } from '../utils/pushToast';

export type ToastProviderToastProps = Omit<ToastProps, 'onClose' | 'children'> & {
  /**
   * Optional duration, indicates how long toast will be displayed for and defaults 3000ms
   */
  autoHideDuration?: null | number;
  /**
   * Optional function to be called when the toast is closed by clicking the close icon
   * useful in the case of needing to perform an action when the toast is closed
   * NOTE: this function only gets called when the close icon is clicked
   */
  onToastClose?: () => void;
  /**
   * Optional subtitle to be displayed in the toast
   */
  subtitle?: string;
};

export interface ToastContextProps {
  /**
   * Provides full control over what is displayed in the toast
   */
  setToast: (toast: ToastProviderToastProps) => void;
  /**
   * Convenience method for showing errors, allows just a string to be provided
   */
  showError: (message: string) => void;
  /**
   * Convenience method for showing success, allows just a string to be provided
   */
  showSuccess: (message: string) => void;
  /**
   * Clears current toast
   */
  closeToast: () => void;
}

/**
 * As the provider will wrap the app, it needs to accepts children
 */
export interface ToastProviderProps {
  /**
   * React node as children
   */
  children: React.ReactNode;
}

export const ToastContext = createContext<ToastContextProps>({
  setToast: () => {},
  showError: () => {},
  showSuccess: () => {},
  closeToast: () => {},
});

/**
 * Hook for obtaining the toast function from the context
 */
export const useToast = (): ToastContextProps => {
  const context = useContext(ToastContext);

  if (context === undefined) {
    throw new Error('ToastContext must be used within a ToastProvider component');
  }
  return context;
};

// Internal provider that has access to useSnackbar (must be inside SnackbarProvider)
const ToastContextWrapper: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const contextValue: ToastContextProps = {
    setToast: (props) => pushToast({ ...props, enqueueSnackbar }),
    showError: (message) => pushToast({ severity: 'error', title: message, enqueueSnackbar }),
    showSuccess: (message) => pushToast({ severity: 'success', title: message, enqueueSnackbar }),
    closeToast: () => closeSnackbar(),
  };

  return <ToastContext.Provider value={contextValue}>{children}</ToastContext.Provider>;
};

export const ToastProvider = ({ children }: ToastProviderProps): JSX.Element => {
  return (
    <SnackbarProvider
      maxSnack={3}
      Components={{
        // Variant mapping to our custom component
        success: ToastSnackbar,
        error: ToastSnackbar,
        info: ToastSnackbar,
        warning: ToastSnackbar,
      }}
    >
      <ToastContextWrapper>{children}</ToastContextWrapper>
    </SnackbarProvider>
  );
};

export type { ToastProps };
